import { useAsyncData, useNuxtApp } from '#imports'
import { type Ref, computed } from 'vue'

import type {
  ModelComparisonCompareProduct,
  ModelComparisonCompareTextBlock,
} from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import type { ArticleCard } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'
import {
  cleanTableData,
  getScoreField,
  getSpecificationField,
  getSpecificationFields,
  getTextBlockField,
} from '@backmarket/nuxt-layer-recommendation/comparisonHelper.ts'
import type { Scores } from '@backmarket/nuxt-layer-recommendation/models/product'
import type { TableData } from '@backmarket/nuxt-layer-recommendation/models/productComparison'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useFetchInternalLinkCards } from './useFetchInternalLinkCards'
import {
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
} from './useFetchModelComparisonV2.constants'
import translations from './useFetchModelComparisonV2.translations'

type ProductSpecification = {
  field: string
  display: string
  values: Array<{
    label: string
  }>
}

type ReviewRating = {
  averageRate: number
  count: number
}

type Product = {
  product: ModelComparisonCompareProduct
  review?: ReviewRating
  specifications: ProductSpecification[]
}

type ComparisonData = {
  products: ModelComparisonCompareProduct[]
  textBlocks: ModelComparisonCompareTextBlock[]
}

export function useFetchModelsComparisonV2(
  comparisonData: Ref<ComparisonData | undefined>,
  comparisonPending: Ref<boolean>,
) {
  const $httpFetch = useHttpFetch()
  const nuxtApp = useNuxtApp()
  const i18n = useI18n()
  const fetchInternalLinkCards = useFetchInternalLinkCards()

  async function fetchReviews(productId: string): Promise<ReviewRating | null> {
    try {
      const response = await $httpFetch(
        '/reviews/v1/products/:productId/reviews-summary',
        {
          pathParams: { productId },
        },
      )

      return response as ReviewRating
    } catch (err) {
      return null
    }
  }

  async function fetchTechnicalSpecs(
    productId: string,
  ): Promise<ProductSpecification[]> {
    const response = await $httpFetch(
      '/bm/product/:productId/technical_specifications',
      {
        pathParams: { productId },
      },
    )

    return (response.specifications ?? []) as ProductSpecification[]
  }

  // Fetch and enrich products with reviews and specifications
  const { data: enrichedData, pending: enrichmentPending } = useAsyncData(
    () => {
      const productIds = comparisonData.value?.products.map((p) => p.productId)

      return `models-comparison-enrichment-${productIds?.join('-')}`
    },
    async () => {
      if (!comparisonData.value?.products) {
        return {
          products: [],
          relatedArticles: [],
          relatedComparePages: [],
        }
      }

      const compareProducts = comparisonData.value.products
      const productIds = compareProducts.map((p) => p.productId)

      const [
        reviews,
        technicalSpecifications,
        { comparePageRelatedArticles, comparePageRelatedComparePages },
      ] = await Promise.all([
        Promise.all(
          productIds.map((productId) =>
            nuxtApp.runWithContext(() => fetchReviews(productId)),
          ),
        ),
        Promise.all(
          productIds.map((productId) =>
            nuxtApp.runWithContext(() => fetchTechnicalSpecs(productId)),
          ),
        ),
        nuxtApp.runWithContext(fetchInternalLinkCards),
      ])

      const products = compareProducts.map(
        (product, index): Product => ({
          product,
          review: reviews[index] ?? undefined,
          specifications: technicalSpecifications[index] ?? [],
        }),
      )

      return {
        products,
        relatedArticles: comparePageRelatedArticles,
        relatedComparePages: comparePageRelatedComparePages,
      }
    },
    {
      default: () => ({
        products: [] as Product[],
        relatedArticles: [] as ArticleCard[],
        relatedComparePages: [] as ArticleCard[],
      }),
    },
  )

  const textBlocks = computed(() => comparisonData.value?.textBlocks ?? [])
  const relatedArticles = computed(() => enrichedData.value.relatedArticles)
  const relatedComparePages = computed(
    () => enrichedData.value.relatedComparePages,
  )
  const pending = computed(
    () => comparisonPending.value || enrichmentPending.value,
  )

  const tableData = computed<TableData[]>(() => {
    const products = enrichedData.value?.products
    if (!products) return []

    const productsSpecifications = products.map((p) => p.specifications)
    const productsScores: Scores[] = products.map((p) =>
      Object.fromEntries(p.product.scores.map((s) => [s.id, String(s.value)])),
    )

    const res: TableData[] = [
      {
        sections: [
          {
            textBlock: getTextBlockField(textBlocks.value, 'introduction'),
            lines: [
              {
                type: 'ratings',
                title: i18n(translations.ratings),
                values: products.map((p) => p.review?.averageRate ?? 0),
              },
            ],
          },
        ],
      },
      {
        title: i18n(translations.scores),
        sections: [
          {
            title: i18n(translations.durability),
            trackingKey: 'comparison_tool_durability',
            textBlock: getTextBlockField(
              textBlocks.value,
              'life_expectancy_score',
            ),
            lines: [
              getScoreField(productsScores, 'life_expectancy_score'),
              ...PRODUCT_COMPARISON_DURABILITY_FIELDS.map((field) =>
                getSpecificationField(productsSpecifications, field),
              ),
            ],
          },
          {
            title: i18n(translations.performance),
            trackingKey: 'comparison_tool_performance',
            textBlock: getTextBlockField(
              textBlocks.value,
              'performances_score',
            ),
            lines: [
              getScoreField(productsScores, 'performances_score'),
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.screenQuality),
            trackingKey: 'comparison_tool_screen_quality',
            textBlock: getTextBlockField(
              textBlocks.value,
              'screen_quality_score',
            ),
            lines: [
              getScoreField(productsScores, 'screen_quality_score'),
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.soundQuality),
            trackingKey: 'comparison_tool_sound_quality',
            textBlock: getTextBlockField(
              textBlocks.value,
              'sound_quality_score',
            ),
            lines: [
              getScoreField(productsScores, 'sound_quality_score'),
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.multimedia),
            trackingKey: 'comparison_tool_multimedia',
            textBlock: getTextBlockField(textBlocks.value, 'multimedia_score'),
            lines: [
              getScoreField(productsScores, 'multimedia_score'),
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.camera),
            trackingKey: 'comparison_tool_camera',
            textBlock: getTextBlockField(textBlocks.value, 'camera_score'),
            lines: [
              getScoreField(productsScores, 'camera_score'),
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_CAMERA_FIELDS,
              ),
            ],
          },
        ],
      },
      {
        title: i18n(translations.miscelaneous),
        separator: true,
        sections: [
          {
            trackingKey: 'comparison_tool_miscelaneous',
            textBlock: getTextBlockField(textBlocks.value, 'miscellaneous'),
            lines: [
              ...getSpecificationFields(
                productsSpecifications,
                PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
              ),
            ],
          },
          {
            textBlock: getTextBlockField(textBlocks.value, 'conclusion'),
            lines: [],
          },
        ],
      },
    ]

    return cleanTableData(res)
  })

  return {
    tableData,
    pending,
    relatedArticles,
    relatedComparePages,
  }
}
